import React, {useEffect, useMemo, useState} from 'react';
import {Badge, Button, DatePicker, Dropdown, MenuProps, message, Pagination, Select, Space, Tabs} from 'antd';
import {
  ExportOutlined,
  FilterOutlined,
  ImportOutlined,
  MoreOutlined,
  PlusOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {useAppSelector} from '../../hooks/hooks';
import {useCreateIssue} from '../../hooks/issues/useIssues';
import {useIssuesData} from '../../hooks/issues/useIssuesData';
import IssueGrid, {IssueRecord} from "./grid/IssueGrid";
import IssueStats from "./stats/IssueStats";
import PageContainer from "../../components/common/PageContainer";
import PrimaryButton from "../../components/common/PrimaryButton";
import FilterBarInline, {FilterSelectGroup} from "../../components/filter/FilterBarInline";
import {getGroupSelectedValues, mergeGroupSelection} from "./filter/issueFilterOptions";
import {useTranslation} from "../../hooks/useTranslation";
import {useDateFormat} from "../../hooks/useDateFormat";
import {PAGINATION} from "../../constants/constants";
import {PaginationState} from "../../types";
import {useNavigate} from 'react-router-dom';
import {useSearch} from "../../contexts/SearchContext";
import './issues.less';

const {RangePicker} = DatePicker;

// TODO: tab filtering is applied client-side over the loaded page. Move to the
// search criteria once backend status codes for open/closed/archived are known.
const isClosedStatus = (status?: string): boolean =>
  /clos|cerr|solucion|resuel|resolv/i.test(status || '');
const isArchivedStatus = (status?: string): boolean =>
  /archiv/i.test(status || '');

const Issues: React.FC = () => {
  const isAuthenticated = useAppSelector(state => state.session.isAuthenticated);
  const user = useAppSelector(state => state.session.user);
  const {t} = useTranslation();
  const {dateFormat} = useDateFormat();
  const navigate = useNavigate();

  // Use SearchContext instead of the local filter state
  const {filters, handleFilterSearch, clearAllSearches} = useSearch();

  // Local state management - only pagination and selections now
  const [pagination, setPagination] = useState<PaginationState>({
    current: PAGINATION.DEFAULT_PAGE,
    pageSize: PAGINATION.DEFAULT_PAGE_SIZE,
    total: 0
  });

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  // Active status tab
  const [activeTab, setActiveTab] = useState<string>('all');

  // Date filter preset
  const [datePreset, setDatePreset] = useState<string>('anytime');

  // Filter groups currently shown as value selects
  const [activeGroups, setActiveGroups] = useState<string[]>([]);

  // Use custom hook for data fetching and business logic
  const {
    searchResults,
    totalCount,
    issueOptions,
    isSearchLoading,
    isSearchError,
    searchError,
  } = useIssuesData(filters, pagination, isAuthenticated);

  // Mutations
  const createIssueMutation = useCreateIssue();

  useEffect(() => {
    if (isSearchError && searchError) {
      message.error(t('issues.messages.loadError'));
    }
  }, [isSearchError, searchError, t]);

  const issueRows = searchResults as IssueRecord[];

  const isAssignedToMe = (row: IssueRecord): boolean => {
    const assignee = row.reportingUser;
    if (!assignee || !user) return false;
    return (
      (!!assignee.username && assignee.username === user.username) ||
      (!!assignee.id && assignee.id === user.id)
    );
  };

  const filterRowsByTab = (rows: IssueRecord[], tab: string): IssueRecord[] => {
    switch (tab) {
      case 'open':
        return rows.filter(r => !isClosedStatus(r.statusDescription) && !isArchivedStatus(r.statusDescription));
      case 'closed':
        return rows.filter(r => isClosedStatus(r.statusDescription));
      case 'archived':
        return rows.filter(r => isArchivedStatus(r.statusDescription));
      case 'assignedToMe':
        return rows.filter(isAssignedToMe);
      default:
        return rows;
    }
  };

  const tabFilteredResults = useMemo(
    () => filterRowsByTab(issueRows, activeTab),
    [issueRows, activeTab, user]
  );

  const tabCounts = useMemo(() => ({
    all: totalCount || issueRows.length,
    open: filterRowsByTab(issueRows, 'open').length,
    assignedToMe: filterRowsByTab(issueRows, 'assignedToMe').length,
    closed: filterRowsByTab(issueRows, 'closed').length,
    archived: filterRowsByTab(issueRows, 'archived').length
  }), [issueRows, totalCount, user]);

  // Action handlers
  const handleCreateIssue = async () => {
    try {
      navigate('/detail/new');
    } catch (error) {
      message.error(t('issues.messages.createError'));
    }
  };

  const handleExport = () => {
    message.info(t('issues.messages.exportInfo'));
  };

  const handleImport = () => {
    message.info(t('issues.messages.importInfo'));
  };

  // Pagination and selection handlers
  const handleTableChange = (paginationConfig: any) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: totalCount
    });
  };

  const handleRowSelection = (selectedKeys: string[]) => {
    setSelectedRowKeys(selectedKeys);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setPagination(prev => ({...prev, current: 1}));
  };

  // Filter handlers - now use SearchContext
  const handleFilterChange = (key: keyof typeof filters, value: any) => {
    const updatedFilters = {
      ...filters,
      [key]: value,
      hasFilters: true // Set to true when user updates filters
    };

    handleFilterSearch(updatedFilters);

    // Reset to the first page when filters change
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  const clearAllFilters = () => {
    clearAllSearches();
    setDatePreset('anytime');
    setActiveGroups([]);
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  // Filter groups — every group is reachable from the group picker; ticking a
  // group reveals its value select.
  const optionGroups = useMemo(
    () => Array.from(issueOptions.entries()).filter(([, items]) => items && items.length > 0),
    [issueOptions]
  );

  const groupOptions = optionGroups.map(([key]) => ({
    value: key,
    label: t(`issues.options.${key}.name`) || key
  }));

  const handleActiveGroupsChange = (keys: string[]) => {
    // Clear the filter values of any group being removed.
    const removed = activeGroups.filter(k => !keys.includes(k));
    if (removed.length > 0) {
      let updatedOptions = filters.options;
      removed.forEach(k => {
        updatedOptions = mergeGroupSelection(
          {...filters, options: updatedOptions}, k, issueOptions.get(k) || [], []
        );
      });
      handleFilterChange('options', updatedOptions);
    }
    setActiveGroups(keys);
  };

  const valueSelects: FilterSelectGroup[] = activeGroups.map(key => {
    const items = issueOptions.get(key) || [];
    return {
      key,
      label: t(`issues.options.${key}.name`) || key,
      options: items.map(opt => ({
        label: opt.description || opt.value || '',
        value: opt.id || opt.value || opt.description || ''
      })),
      value: getGroupSelectedValues(filters, key),
      onChange: (values) => handleFilterChange('options', mergeGroupSelection(filters, key, items, values))
    };
  });

  const activeFilterCount = filters.options.length + (filters.dateRange ? 1 : 0);

  // Date preset → createdDate upper bound (empty lower bound = open-ended).
  const handleDatePresetChange = (preset: string) => {
    setDatePreset(preset);
    if (preset === 'custom') {
      handleFilterChange('dateRange', null);
      return;
    }
    const upperBound: Record<string, dayjs.Dayjs> = {
      olderThanWeek: dayjs().subtract(1, 'week'),
      olderThanMonth: dayjs().subtract(1, 'month'),
      olderThan6Months: dayjs().subtract(6, 'month'),
      olderThanYear: dayjs().subtract(1, 'year')
    };
    const bound = upperBound[preset];
    handleFilterChange('dateRange', bound ? ['', bound.format(dateFormat)] : null);
  };

  const dateControl = (
    <Space size={8}>
      <Select
        size="medium"
        variant={"filled"}
        style={{minWidth: 170}}
        value={datePreset}
        onChange={(value) => handleDatePresetChange(value as string)}
        options={[
          {value: 'anytime', label: t('issues.date.anytime')},
          {value: 'olderThanWeek', label: t('issues.date.olderThanWeek')},
          {value: 'olderThanMonth', label: t('issues.date.olderThanMonth')},
          {value: 'olderThan6Months', label: t('issues.date.olderThan6Months')},
          {value: 'olderThanYear', label: t('issues.date.olderThanYear')},
          {value: 'custom', label: t('issues.date.customRange')}
        ]}
      />
      {datePreset === 'custom' && (
        <RangePicker
          size="small"
          format={dateFormat}
          value={filters.dateRange ? [dayjs(filters.dateRange[0]), dayjs(filters.dateRange[1])] : null}
          onChange={(dates, dateStrings) =>
            handleFilterChange('dateRange', dates ? dateStrings : null)
          }
        />
      )}
    </Space>
  );

  // Menu configuration
  const moreActionsMenu: MenuProps = {
    items: [
      {
        key: 'bulk-update',
        label: t('buttons.bulkUpdate'),
        icon: <FilterOutlined/>
      },
      {
        key: 'archive',
        label: t('buttons.archiveSelected'),
      },
      {
        key: 'refresh',
        label: t('buttons.refreshData')
      }
    ]
  };

  // Tabs
  const tabItems = (['all', 'open', 'assignedToMe', 'closed', 'archived'] as const).map(key => ({
    key,
    label: (
      <Space size={6}>
        {t(`issues.tabs.${key}`)}
        <Badge
          count={tabCounts[key]}
          showZero
          overflowCount={9999}
          color={activeTab === key ? undefined : '#bcc4d3'}
          style={{
            backgroundColor: activeTab === key ? '#ECF8FF' : undefined,
            borderColor: activeTab === key ? '#91CAFF' : undefined,
            color: activeTab === key ? '#0769D9' : undefined,
            fontWeight: activeTab === key ? 'bold' : undefined
          }}
        />
      </Space>
    )
  }));

  // Early return for unauthenticated users
  if (!isAuthenticated) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px'
      }}>
        <div>
          Please authenticate to view issues
        </div>
      </div>
    );
  }

  const resultLabel = totalCount === 1 ? `${t('filters.result')}` : `${t('filters.results')}`;

  const gridPagination: PaginationState = activeTab === 'all'
    ? {...pagination, total: totalCount}
    : {...pagination, total: tabFilteredResults.length};

  return (
    <PageContainer
      title={t('issues.title')}
      subTitle={t('issues.subtitle')}
      className="issues-container"
      bordered={false}
      headerPaddingBottom={true}
      extra={
        <Space>
          <PrimaryButton
            type="primary"
            icon={<PlusOutlined/>}
            onClick={handleCreateIssue}
          >
            {t('issues.createIssue')}
          </PrimaryButton>
          <PrimaryButton
            icon={<ExportOutlined/>}
            onClick={handleExport}
          >
            {t('buttons.export')}
          </PrimaryButton>
          <PrimaryButton
            icon={<ImportOutlined/>}
            onClick={handleImport}
          >
            {t('buttons.import')}
          </PrimaryButton>
          <Dropdown menu={moreActionsMenu} trigger={['click']}>
            <Button icon={<MoreOutlined/>}/>
          </Dropdown>
        </Space>
      }
    >
      {/* Stat's row */}
      {/*<IssueStats/>*/}

      {/* Status tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        className="gt-issue-tabs"
        tabBarStyle={{margin: 0}}
      />

      {/* Filter Bar */}
      <FilterBarInline
        groupOptions={groupOptions}
        activeGroups={activeGroups}
        onActiveGroupsChange={handleActiveGroupsChange}
        groupsLabel={t('issues.filters')}
        selects={valueSelects}
        dateControl={dateControl}
        totalCount={totalCount}
        resultLabel={resultLabel}
        activeCount={activeFilterCount}
        clearLabel={t('buttons.clear')}
        onClearAll={clearAllFilters}
        extra={
          <Pagination
            simple
            size="small"
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={gridPagination.total}
            showSizeChanger={false}
            onChange={(page, pageSize) =>
              handleTableChange({current: page, pageSize})
            }
          />
        }
      />

      {/* Issues Grid */}
      <div style={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}}>
        <IssueGrid
          dataSource={tabFilteredResults}
          loading={isSearchLoading}
          selectedRowKeys={selectedRowKeys}
          onRowSelection={handleRowSelection}
        />
      </div>
    </PageContainer>
  );
};

export default Issues;
